import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AbstractControl, FormBuilder, FormControl, FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { combineLatest, defer, Observable, of, OperatorFunction, filter, map, shareReplay, switchMap, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User } from 'src/app/core/modules/users/users.types';
import { UsersService } from '../user.service';
import { AuthorizedUserInfo } from '../users-page/users-page.types';
import { UtilsService } from '../../../core/services/utils.service';
import { ConfirmBySmsComponent } from '../../../shared/components/modals/confirm-by-sms/confirm-by-sms.component';
import { ValidationService } from '../../../shared/components/form-controls/validators/validation.service';
import { ModalService } from '../../../core/modal/modal.service';
import { PageHeaderDropdownItem } from '../../../shared/page-header/page-header.model';
import { AlertsService } from '../../../core/services/alerts.service';
import { OrganisationType } from '../../../core/models/organisation.model';
import { Permission } from '../../../core/modules/admin/admin.types';

export interface UserFormModel {
  email: FormControl<string>;
  phone: FormControl<string>;
  iin: FormControl<string>;
  name: FormControl<string | undefined>;
  position: FormControl<string | undefined>;
  login: FormControl<string>;
  secret: FormControl<string | undefined>;
  confirmSecret: FormControl<string | undefined>;
  oldSecret?: FormControl<string>;
  permissions: FormControl<Permission[]>;
  retailPlaces: FormControl<number[]>;
  groupsIds: FormControl<number[]>;
}

@UntilDestroy()
@Component({
  selector: 'tax-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.sass'],
})
// TODO need to be refactored
export class UserComponent implements OnInit {
  dropdownItems: PageHeaderDropdownItem[] = [];
  isUserExist = false;
  user?: User;
  userForm: FormGroup<UserFormModel> = this.getForm();

  private isAuthedUserHasRootPermission: boolean | null = null;
  private authUserInfo$: Observable<AuthorizedUserInfo> = this.usersService.getCurrentUserInfo()
    .pipe(shareReplay({
      refCount: true,
    }));

  user$: Observable<User | undefined> = combineLatest([
    this.route.params,
    this.authUserInfo$,
  ])
    .pipe(
      tap(([routeParams, authUser]) => {
        this.isAuthedUserHasRootPermission = authUser.permissions.includes('default.root');
        this.userId = parseInt(routeParams.id, 10);
        this.isUserExist = Boolean(this.userId);
      }),
      /**
       * Класс используется и для редактирования пользователя и для создания. Поэтому надо чтобы поток доходил до конца
       */
      switchMap(([routeParams, authUser]) => defer(() => (routeParams.id === '0'
        ? of(undefined)
        : this.getUserInfo(routeParams.id)))),
      shareReplay({
        refCount: true,
      }),
    );

  private userId?: number;

  constructor(
    private alertsService: AlertsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private usersService: UsersService,
    private utilsService: UtilsService,
    private modalService: ModalService,
    private validationService: ValidationService,
  ) {
  }

  ngOnInit(): void {
    this.user$.pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.user = user;
      });
  }

  back(): void {
    this.location.back();
  }

  generatePassword(): void {
    const newPassword = this.utilsService.generatePassword();
    this.userForm.patchValue({
      secret: newPassword, confirmSecret: newPassword,
    });
  }

  getFirstLetterOfName(userName?: string | null): string {
    return userName ? userName.charAt(0).toLocaleUpperCase() : '?';
  }

  editUser(user?: User): void {
    if (this.userForm?.invalid) {
      return;
    }

    this.usersService.editUser({
      ...user,
      ...this.userForm.getRawValue(),
      iin: this.userForm.value.iin || undefined,
      kkmGroupsPermissions: [],
    })
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.router.navigate(['main/users'])
            .then(() => {
              this.alertsService.addSuccessAlert({
                message: this.isUserExist ? 'user.update.success' : 'user.create.success',
              });
            });
        },
      );
  }

  isDisallowEditPhone(user?: User): boolean {
    return !this.isAuthedUserHasRootPermission && Boolean(user?.isRoot);
  }

  onToggleFullRetailPlaceAccess(isFullRetailPlacesAccess: boolean): void {
    let { permissions } = this.userForm.getRawValue();

    if (isFullRetailPlacesAccess) {
      permissions.push('retail.*');
    } else {
      permissions = permissions.filter(permission => permission !== 'retail.*');
    }

    this.userForm.patchValue({
      permissions,
    });
  }

  setPhone(): void {
    this.modalService.openOld<string | null>(ConfirmBySmsComponent, {
      title: this.isUserExist ? 'user.phone.change.modal.title' : 'user.phone.add.modal.title',
      buttonText: this.isUserExist ? 'button.change' : 'button.add',
      phoneValidator: this.validationService.samePhoneValidator(this.user?.phone),
      userId: this.userId,
    })
      .pipe(
        filter(userPhone => Boolean(userPhone)) as OperatorFunction<string | null, string>,
        untilDestroyed(this),
      )
      .subscribe((userPhone) => {
        this.userForm.patchValue({
          phone: userPhone,
        });
      });
  }

  updatePermissionsValue(permissions: Permission[]): void {
    this.userForm.patchValue({
      permissions,
    });
  }

  updateSelectedGroups(groupsIds: number[]): void {
    this.userForm.patchValue({
      groupsIds,
    });
  }

  updateSelectedRetailPlaces(retailPlacesIds: number[]): void {
    this.userForm.patchValue({
      retailPlaces: retailPlacesIds,
    });
  }

  private getDropdownItems(user: User): PageHeaderDropdownItem[] {
    return [{
      action: () => {
        this.usersService.blockUser(user.login)
          .pipe(untilDestroyed(this))
          .subscribe((response: User) => {
            user.blockedAt = response.blockedAt;
            this.dropdownItems = this.getDropdownItems(user);
          });
      },
      icon: user.blockedAt ? 'done' : 'block',
      title: user.blockedAt ? 'user.unblock' : 'user.block',
      visibilityCondition: this.isUserExist && !user.isRoot,
    }];
  }

  private getForm(user?: User, authUserInfo?: AuthorizedUserInfo): FormGroup<UserFormModel> {
    const iin = user?.isRoot && authUserInfo?.organisation.type.toString() === OrganisationType.Individual
      ? authUserInfo.organisation.inn
      : user?.iin;

    const form = this.fb.group<UserFormModel>({
      email: new FormControl(user?.email || '', {
        nonNullable: true, validators: [Validators.required, this.validationService.isEmailCorrect],
      }),
      phone: new FormControl(user?.phone || '', {
        nonNullable: true, validators: [Validators.required],
      }),
      iin: new FormControl({
        value: iin || '',
        disabled: Boolean(user?.isRoot) && authUserInfo?.organisation.type.toString() === OrganisationType.Individual,
      }, {
        nonNullable: true, validators: [this.validationService.validateInn],
      }),
      name: new FormControl(user?.name, {
        nonNullable: true,
      }),
      position: new FormControl(user?.position, {
        nonNullable: true,
      }),
      login: new FormControl({
        value: user?.login || '', disabled: Boolean(user?.isRoot),
      }, {
        nonNullable: true, validators: user?.isRoot ? [Validators.required] : [Validators.required, this.isLoginCorrect],
      }),
      secret: new FormControl('', {
        nonNullable: true, validators: [this.validationService.isPasswordStrength],
      }),
      confirmSecret: new FormControl('', {
        nonNullable: true,
      }),
      permissions: new FormControl(
        user?.permissions || [],
        {
          nonNullable: true,
        },
      ),
      retailPlaces: new FormControl(user?.retailPlaces || [], {
        nonNullable: true,
      }),
      groupsIds: new FormControl(user?.groupsIds || [], {
        nonNullable: true,
      }),
    });

    this.validateConfirmSecret(form);

    return form;
  }

  private getUserInfo(editedUserId: string): Observable<User> {
    return combineLatest([
      this.usersService.getUser(Number(editedUserId)),
      this.authUserInfo$,
    ])
      .pipe(
        tap(([user, authUserInfo]) => {
          this.userForm = this.getForm(user, authUserInfo);
          this.dropdownItems = this.getDropdownItems(user);
          this.updateFormControls(user);
        }),
        map(([user, authUserInfo]) => user),
      );
  }

  private isLoginCorrect(control: AbstractControl): ValidationErrors | null {
    const isValid = control.value?.length > 4 && Boolean(control.value?.match(/[A-z]/g));

    return isValid
      ? null
      : {
        isEqual: false,
        errorMessage: 'user.form.incorrect.login',
      };
  }

  private updateFormControls(user: User): void {
    this.authUserInfo$
      .pipe(untilDestroyed(this))
      .subscribe((authUser) => {
        const isNeedOldPassword = authUser.permissions.includes('default.root') || Boolean(user.isRoot);
        if (isNeedOldPassword) {
          this.userForm.addControl('oldSecret', new FormControl('', {
            nonNullable: true, validators: [Validators.required, this.validationService.isPasswordStrength],
          }));
        }
      });
  }

  private validateConfirmSecret(form: FormGroup): void {
    const passwordControl = form.controls.secret;
    const confirmControl = form.controls.confirmSecret;

    confirmControl.addValidators(this.validationService.isEqual.bind(this, passwordControl));

    passwordControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        confirmControl.markAsTouched();
        confirmControl.updateValueAndValidity();
      });
  }
}
